import { apiClient } from './authService';

const adminService = {
  async listTiers() {
    const res = await apiClient.get('/admin/tiers');
    return res.data?.tiers || [];
  },

  async getMaterialized(tierCode) {
    const res = await apiClient.get(`/admin/tiers/${encodeURIComponent(tierCode)}/materialized`);
    return res.data;
  },

  async listTierParameters(tierCode) {
    const res = await apiClient.get(`/admin/tier-config/${encodeURIComponent(tierCode)}/parameters`);
    return res.data;
  },

  async listTierSteps(tierCode) {
    const res = await apiClient.get(`/admin/tier-config/${encodeURIComponent(tierCode)}/steps`);
    return res.data;
  },

  async updateTierParameter(tierCode, paramKey, payload) {
    const res = await apiClient.patch(
      `/admin/tier-config/${encodeURIComponent(tierCode)}/parameters/${encodeURIComponent(paramKey)}`,
      payload
    );
    return res.data;
  },

  async updateTierStep(tierCode, stepCode, payload) {
    const res = await apiClient.patch(
      `/admin/tier-config/${encodeURIComponent(tierCode)}/steps/${encodeURIComponent(stepCode)}`,
      payload
    );
    return res.data;
  },

  async batchApplyTierConfig(tierCode, payload) {
    const res = await apiClient.post(
      `/admin/tier-config/${encodeURIComponent(tierCode)}/batch-apply`,
      payload
    );
    return res.data;
  },

  async listUsers({ page = 1, pageSize = 25 } = {}) {
    const res = await apiClient.get('/admin/users', { params: { page, page_size: pageSize } });
    return res.data;
  },

  async getUser(userId) {
    const res = await apiClient.get(`/admin/users/${userId}`);
    return res.data;
  },

  async updateUserTier(userId, newTier) {
    const res = await apiClient.put(`/admin/users/${userId}/tier`, { new_tier: newTier });
    return res.data;
  },

  async deactivateUser(userId) {
    const res = await apiClient.post(`/admin/users/${userId}/deactivate`);
    return res.data;
  },

  async reactivateUser(userId) {
    const res = await apiClient.post(`/admin/users/${userId}/reactivate`);
    return res.data;
  },

  async unlockUser(userId) {
    const res = await apiClient.post(`/admin/users/${userId}/unlock`);
    return res.data;
  },

  async revokeUserSessions(userId) {
    const res = await apiClient.post(`/admin/users/${userId}/revoke-sessions`);
    return res.data;
  },

  async getMonitoringHealthDetailed() {
    const res = await apiClient.get('/monitoring/health/detailed');
    return res.data;
  },

  async getMonitoringMetrics() {
    const res = await apiClient.get('/monitoring/metrics');
    return res.data;
  },

  async getDatabasePoolStatus() {
    const res = await apiClient.get('/monitoring/database/pool');
    return res.data;
  },

  async getUserJobMetrics({
    page = 1,
    pageSize = 50,
    search = '',
    tierFilter = '',
    statusFilter = 'all',
    activeOnly = null,
  } = {}) {
    const params = {
      page,
      page_size: pageSize,
      status_filter: statusFilter || 'all',
    };
    if (search) {
      params.search = search;
    }
    if (tierFilter) {
      params.tier_filter = tierFilter;
    }
    if (activeOnly !== null && activeOnly !== undefined && activeOnly !== '') {
      params.active_only = Boolean(activeOnly);
    }
    const res = await apiClient.get('/admin/jobs/users/metrics', { params });
    return res.data;
  },

  async clearUserJobs(userId, statuses = ['pending', 'failed']) {
    const res = await apiClient.post(`/admin/jobs/users/${userId}/clear`, {
      statuses,
    });
    return res.data;
  },

  async clearUserStaleProcessingJobs(userId, olderThanHours = 48) {
    const res = await apiClient.post(`/admin/jobs/users/${userId}/clear-stale-processing`, {
      older_than_hours: olderThanHours,
    });
    return res.data;
  },

  async createUser({ username, email, password }) {
    // Use auth API but do NOT store tokens; call directly via apiClient
    const res = await apiClient.post('/auth/register', { username, email, password });
    // Return created user without touching local storage
    return res.data?.user;
  },

  async getSystemConfig() {
    const res = await apiClient.get('/admin/config');
    return res.data;
  },

  async updateSystemConfig(configKey, configValue, description = '') {
    const serializedValue = typeof configValue === 'string'
      ? configValue
      : JSON.stringify(configValue);
    const endpoint = `/admin/config/${encodeURIComponent(configKey)}`;
    const bodyPayload = {
      config_value: serializedValue,
      description: description || undefined,
    };

    try {
      const res = await apiClient.put(endpoint, bodyPayload);
      return res.data;
    } catch (error) {
      const status = error?.response?.status;
      if (status !== 400 && status !== 404 && status !== 405 && status !== 415 && status !== 422) {
        throw error;
      }

      // Backward compatibility for servers that still only accept query params.
      const fallback = await apiClient.put(endpoint, null, {
        params: {
          config_value: serializedValue,
          description: description || undefined,
        },
      });
      return fallback.data;
    }
  },
};

export default adminService;
